import classNames from 'classnames';
import React__default, { useState, useRef, useEffect, useCallback } from 'react';
import './Image.scss';

/* Track URLs that have successfully loaded so remounts can optimistically skip loading.
   LRU cache — evicts the least-recently-used entry when capacity is exceeded. */
const LRU_CAPACITY = 200;
const loadedUrls = new Map();
function lruHas(url) {
    if (!loadedUrls.has(url))
        return false;
    // Move to end (most-recently-used) on access
    loadedUrls.delete(url);
    loadedUrls.set(url, true);
    return true;
}
function lruAdd(url) {
    if (loadedUrls.has(url))
        loadedUrls.delete(url);
    loadedUrls.set(url, true);
    if (loadedUrls.size > LRU_CAPACITY) {
        // Map iterator yields in insertion order — first key is the LRU entry
        loadedUrls.delete(loadedUrls.keys().next().value);
    }
}
function lruDelete(url) {
    loadedUrls.delete(url);
}
const Image = (props) => {
    const { className, ariaLabel, rounding = '10', aspectRatio, alt, onError, onLoad, ariaHidden, hideLoadingState = false, width, height, hasBackground = false, objectFit = 'cover', ...rest } = props;
    const isValidSrc = !!props.src && props.src.trim() !== '';
    /* Always start loading so the image is hidden (opacity: 0) until ready.
       The ref callback or handleLoad will reveal it with a fade-in transition.
       Cache hits use a faster fade via data-rev-cached. */
    const [loading, setLoading] = useState(isValidSrc);
    const [error, setError] = useState(!isValidSrc);
    const [cached, setCached] = useState(() => isValidSrc && lruHas(props.src));
    const imgRef = useRef(null);
    useEffect(() => {
        if (!isValidSrc) {
            setError(true);
            setLoading(false);
            setCached(false);
            return;
        }
        setError(false);
        setLoading(true);
        setCached(lruHas(props.src));
    }, [props.src, isValidSrc]);
    const reveal = useCallback(() => {
        setLoading(false);
        setError(false);
    }, []);
    const handleLoad = useCallback((e) => {
        const img = e.currentTarget;
        if (props.src)
            lruAdd(props.src);
        // decode() ensures the image is fully rasterized before we crossfade it in
        img.decode().then(reveal, reveal);
        if (onLoad)
            onLoad(e);
    }, [props.src, onLoad, reveal]);
    const handleError = useCallback((e) => {
        if (props.src)
            lruDelete(props.src);
        setLoading(false);
        setError(true);
        if (onError)
            onError(e);
    }, [props.src, onError]);
    const imgRefCallback = useCallback((node) => {
        imgRef.current = node;
        if (!node || !props.src || error)
            return;
        if (node.complete && node.naturalWidth > 0) {
            /* Browser has the pixels — reveal with the fade-in transition. */
            lruAdd(props.src);
            reveal();
        }
    }, [props.src, error, reveal]);
    const showLoading = loading && !hideLoadingState;
    return (React__default.createElement("div", { "data-rev-component": 'image', "data-rev-rounding": rounding, "data-rev-loading": loading, "data-rev-error": error, "data-rev-hide-loading": hideLoadingState || undefined, "data-rev-cached": cached || undefined, "data-rev-background": hasBackground || undefined, className: classNames(showLoading && 'is-loading', className), "aria-hidden": ariaHidden, style: {
            '--aspectRatio': aspectRatio,
            '--width': typeof width === 'number' ? `${width}px` : width,
            '--height': typeof height === 'number' ? `${height}px` : height,
            '--objectFit': objectFit,
        } },
        !hideLoadingState && React__default.createElement("div", { className: 'image-skeleton--rev' }),
        React__default.createElement("img", { ...rest, ref: imgRefCallback, src: isValidSrc && !error ? props.src : EMPTY_IMAGE, className: 'image-element--rev', "aria-label": ariaLabel, "aria-busy": loading, alt: !loading && !error ? alt : undefined, onLoad: !error ? handleLoad : undefined, onError: !error ? handleError : undefined })));
};
const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAPcAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH5BAEAAP8ALAAAAAABAAEAAAgEAP8FBAA7';

export { Image };
//# sourceMappingURL=Image.js.map
